import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useAuth } from '@/lib/auth/useAuth';
import { useCircle } from '@/lib/circle/useCircle';
import { useConversations } from '@/hooks/useMessaging';

// Lightweight, device-local unread tracking for Messages. We persist a
// per-user, per-circle map of { conversationId: lastSeenISO } in localStorage
// and treat a conversation as unread when its updated_at is newer than what
// we've seen. Sending a message or viewing a thread marks it seen, so a user
// never sees their own messages counted as unread. This isn't a server-side
// read receipt — just enough to light up the nav badge the testers asked for.

interface UnreadValue {
  seen: Record<string, string>;
  markSeen: (conversationId: string, ts?: string) => void;
}

const UnreadContext = createContext<UnreadValue | null>(null);

function storageKeyFor(userId: string | null, circleId: string | null): string | null {
  return userId && circleId ? `careos:msgseen:${userId}:${circleId}` : null;
}

export function MessagesUnreadProvider({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  const { currentCircle } = useCircle();
  const storageKey = storageKeyFor(user?.id ?? null, currentCircle?.id ?? null);

  const [seen, setSeen] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!storageKey) {
      setSeen({});
      return;
    }
    try {
      setSeen(JSON.parse(localStorage.getItem(storageKey) ?? '{}') as Record<string, string>);
    } catch {
      setSeen({});
    }
  }, [storageKey]);

  const markSeen = useCallback(
    (conversationId: string, ts?: string) => {
      if (!storageKey) return;
      setSeen((prev) => {
        const stamp = ts ?? new Date().toISOString();
        if (prev[conversationId] && prev[conversationId] >= stamp) return prev;
        const next = { ...prev, [conversationId]: stamp };
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          /* storage may be unavailable (private mode) — badge just won't persist */
        }
        return next;
      });
    },
    [storageKey],
  );

  const value = useMemo(() => ({ seen, markSeen }), [seen, markSeen]);
  return <UnreadContext.Provider value={value}>{children}</UnreadContext.Provider>;
}

export function useMessagesSeen(): UnreadValue {
  const ctx = useContext(UnreadContext);
  if (!ctx) throw new Error('useMessagesSeen must be used within MessagesUnreadProvider');
  return ctx;
}

/** Count of conversations in the current circle with activity newer than seen. */
export function useMessagesUnreadCount(): number {
  const { currentCircle } = useCircle();
  const { data: conversations = [] } = useConversations(currentCircle?.id ?? null);
  const { seen } = useMessagesSeen();
  return conversations.filter((c) => {
    const s = seen[c.id];
    return !s || c.updated_at > s;
  }).length;
}
